<script lang="ts">
  import { formatMoney, perMonth as perMonthEquivalent, offerMode, type Plan } from "$lib/pricing";

  interface Props {
    plan: Plan;
    selectable?: boolean;
    selected?: boolean;
    onselect?: () => void;
  }

  let { plan, selectable = false, selected = false, onselect }: Props = $props();

  const mode = $derived(offerMode(plan));
  const money = (cents: number, withCents?: boolean) => formatMoney(cents, plan.currency, withCents);
  const perMonth = (cents: number) => perMonthEquivalent(cents, plan.currency);
</script>

<svelte:element
  this={selectable ? "button" : "div"}
  type={selectable ? "button" : undefined}
  role={selectable ? "radio" : undefined}
  aria-checked={selectable ? selected : undefined}
  onclick={selectable ? onselect : undefined}
  class="flex w-full flex-col gap-3 rounded-xl border bg-card p-6 text-left shadow-sm transition
    {selectable ? 'cursor-pointer hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring' : ''}
    {selected ? 'border-primary ring-2 ring-primary' : 'border-border'}"
>
  <div class="space-y-1">
    <p class="text-lg font-semibold">{plan.name}</p>
    {#if plan.description}
      <p class="text-sm text-muted-foreground">{plan.description}</p>
    {/if}
  </div>

  <div class="space-y-1">
    {#if mode === "annual_once"}
      <p class="text-3xl font-bold">{perMonth(plan.offer!.amount)}/month</p>
      <p class="text-sm text-muted-foreground">
        {money(plan.offer!.amount)} first year for new subscribers; renews annually at {money(plan.amount)}
      </p>
    {:else if mode === "annual_forever"}
      <p class="flex flex-wrap items-baseline gap-2">
        <span class="text-3xl font-bold">{perMonth(plan.offer!.amount)}/month</span>
        <span class="text-lg text-muted-foreground line-through">{perMonth(plan.amount)}/month</span>
      </p>
      <p class="text-sm text-muted-foreground">
        {money(plan.offer!.amount)}/year for new subscribers, billed once a year
      </p>
    {:else if mode === "monthly_once"}
      <p class="flex flex-wrap items-baseline gap-2">
        <span class="text-3xl font-bold">{money(plan.offer!.amount)} first month</span>
        <span class="text-lg text-muted-foreground line-through">{money(plan.amount)}</span>
      </p>
      <p class="text-sm text-muted-foreground">
        then {money(plan.amount)}/month for new subscribers, billed every month
      </p>
    {:else if mode === "monthly_forever"}
      <p class="flex flex-wrap items-baseline gap-2">
        <span class="text-3xl font-bold">{money(plan.offer!.amount)}/month</span>
        <span class="text-lg text-muted-foreground line-through">{money(plan.amount)}/month</span>
      </p>
      <p class="text-sm text-muted-foreground">for new subscribers, billed every month</p>
    {:else if mode === "monthly_repeating"}
      <p class="flex flex-wrap items-baseline gap-2">
        <span class="text-3xl font-bold">
          {money(plan.offer!.amount)}/month for first {plan.offer!.duration_in_months} months
        </span>
        <span class="text-lg text-muted-foreground line-through">{money(plan.amount)}/month</span>
      </p>
      <p class="text-sm text-muted-foreground">
        then {money(plan.amount)}/month for new subscribers, billed every month
      </p>
    {:else if mode === "annual"}
      <p class="text-3xl font-bold">{perMonth(plan.amount)}/month</p>
      <p class="text-2xl text-muted-foreground">when billed once a year at {money(plan.amount)}</p>
    {:else}
      <p class="text-3xl font-bold">{money(plan.amount)}/month</p>
      <p class="text-sm text-muted-foreground">billed every month</p>
    {/if}
  </div>

  {#if plan.trial_period_days > 0}
    <p class="text-sm text-muted-foreground">billing starts only after your {plan.trial_period_days}-day free trial period</p>
  {/if}

  {#if plan.features.length > 0}
    <ul class="flex flex-col gap-1.5 text-sm">
      {#each plan.features as feature (feature)}
        <li class="flex items-start gap-2">
          <span aria-hidden="true" class="mt-0.5 text-primary">✓</span>
          <span>{feature}</span>
        </li>
      {/each}
    </ul>
  {/if}
</svelte:element>
